/**
 * WAX Account Creator Service
 *
 * Service for creating WAX Cloud Wallet accounts on the WAX blockchain.
 * Uses Wharfkit for secure server-side signing.
 * Migrated from eosjs to address CVE-2024-968p security vulnerability.
 */

import { getWaxConfig } from './config';
import { getWharfkitServerService, getWharfkitRpcClient } from './wharfkit-server';
import { logger } from '../logger';

export interface WaxAccountCreationRequest {
  accountName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  payerAccount: string;
  payerPrivateKey: string;
}

export interface WaxAccountCreationResult {
  success: boolean;
  accountName?: string;
  transactionId?: string;
  error?: string;
}

export class WaxAccountCreator {
  private config = getWaxConfig();

  /**
   * Create a WAX Cloud Wallet account using the WAX faucet
   * Requires 5 WAX payment from a funded account
   */
  async createAccount(request: WaxAccountCreationRequest): Promise<WaxAccountCreationResult> {
    try {
      logger.info("Starting WAX Cloud Wallet account creation", {
        accountName: request.accountName,
        creationType: "cloud_wallet",
      });
      logger.debug("WAX configuration", {
        network: this.config.network,
        rpcEndpoint: this.config.rpcEndpoint,
        chainId: this.config.chainId,
      });
      logger.debug("Account creation request details", {
        accountName: request.accountName,
        email: request.email,
        payerAccount: request.payerAccount,
        payerKeyLength: request.payerPrivateKey?.length || 0,
      });

      // Use Wharfkit server service for transaction signing
      logger.debug("Initializing Wharfkit service");
      const wharfkitService = getWharfkitServerService();
      await wharfkitService.initialize();
      logger.debug("Wharfkit service initialized successfully");

      // Test RPC connection first
      logger.debug("Testing RPC connection");
      const infoResult = await wharfkitService.getInfo();
      if (!infoResult.success) {
        throw new Error(`RPC connection failed: ${infoResult.error}`);
      }
      logger.debug("RPC connection successful", {
        chainId: infoResult.data?.chain_id,
        headBlockNum: infoResult.data?.head_block_num,
      });

      // Test payer account exists
      logger.debug("Verifying payer account exists", { payerAccount: request.payerAccount });
      const accountResult = await wharfkitService.getAccount(request.payerAccount);
      if (!accountResult.success) {
        throw new Error(`Payer account '${request.payerAccount}' not found on blockchain`);
      }
      logger.debug("Payer account verified", {
        accountName: accountResult.data?.account_name,
        created: accountResult.data?.created,
      });

      // Create the account creation transaction using WAX Cloud Wallet creation
      // This requires sending 5 WAX to newuser.wax with the account name in memo
      logger.debug("Preparing transaction");
      const treasuryAccount = await wharfkitService.getTreasuryAccount();

      logger.debug("Transaction details", {
        contract: 'eosio.token',
        action: 'transfer',
        from: treasuryAccount,
        to: 'newuser.wax',
        quantity: '5.00000000 WAX',
        memo: request.accountName,
      });

      // Execute the transaction
      logger.debug("Executing transaction");
      const result = await wharfkitService.transact([
        {
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{
            actor: treasuryAccount,
            permission: 'active',
          }],
          data: {
            from: treasuryAccount,
            to: 'newuser.wax',
            quantity: '5.00000000 WAX',
            memo: request.accountName
          },
        }
      ]);

      if (!result.success) {
        throw new Error(result.error || 'Transaction failed');
      }

      logger.info("WAX account creation transaction successful", {
        accountName: request.accountName,
        transactionId: result.transactionId,
        blockNum: result.blockNumber,
      });

      return {
        success: true,
        accountName: request.accountName,
        transactionId: result.transactionId || 'success_' + Date.now()
      };

    } catch (error) {
      logger.error("WAX account creation failed", {
        accountName: request.accountName,
        errorName: error instanceof Error ? error.name : "Unknown",
        error: error instanceof Error ? error.message : String(error),
      });

      // Parse specific error messages
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        if (error.message.includes('account already exists') || error.message.includes('already taken')) {
          errorMessage = 'Account name already exists or is taken';
        } else if (error.message.includes('insufficient funds') || error.message.includes('overdrawn balance')) {
          errorMessage = 'Insufficient funds in payer account (need 5 WAX)';
        } else if (error.message.includes('invalid memo') || error.message.includes('invalid account name')) {
          errorMessage = 'Invalid account name format for WAX Cloud Wallet';
        } else if (error.message.includes('newuser.wax')) {
          errorMessage = 'WAX Cloud Wallet creation service error - please try again later';
        } else if (error.message.includes('transfer')) {
          errorMessage = 'Transfer to WAX account creation service failed';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Check if an account name is available on mainnet
   */
  async isAccountAvailable(accountName: string): Promise<boolean> {
    try {
      const rpcClient = getWharfkitRpcClient();
      const exists = await rpcClient.accountExists(accountName);
      return !exists;
    } catch (error) {
      logger.error("Error checking account availability", {
        accountName,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      // On error, assume account is not available to be safe
      return false;
    }
  }

  /**
   * Get account balance to check if payer has sufficient funds
   */
  async getAccountBalance(accountName: string): Promise<number> {
    try {
      logger.debug("Checking balance for account", {
        accountName,
        rpcEndpoint: this.config.rpcEndpoint,
      });

      const rpcClient = getWharfkitRpcClient();
      logger.debug("Querying currency balance");
      const result = await rpcClient.getCurrencyBalance(accountName, 'eosio.token', 'WAX');

      logger.debug("Balance query result", { result });

      if (!result.success || !result.data || result.data.length === 0) {
        logger.debug("No WAX balance found for account", { accountName });
        return 0;
      }

      const balance = result.data[0];
      const balanceFloat = parseFloat(balance.split(' ')[0]);

      logger.debug("Account balance retrieved", { accountName, balance: balanceFloat });
      return balanceFloat;

    } catch (error) {
      logger.error("Error getting account balance", {
        accountName,
        rpcEndpoint: this.config.rpcEndpoint,
        error: error instanceof Error ? error.message : String(error),
      });
      return 0;
    }
  }

  /**
   * Validate WAX Cloud Wallet account name format
   */
  isValidAccountName(accountName: string): boolean {
    // WAX Cloud Wallet accounts end with .wam and can contain: a-z and digits 1-5 only
    const regex = /^[a-z1-5]{1,8}\.wam$/;
    return regex.test(accountName);
  }

  /**
   * Generate a WAX Cloud Wallet account name (ends with .wam)
   * Valid characters: .abcdefghijklmnopqrstuvwxyz12345 (note: only digits 1-5, not 0,6,7,8,9)
   */
  generateAccountName(prefix: string = ''): string {
    // WAX Cloud Wallet accounts can only contain: a-z and digits 1-5 (NOT 0,6,7,8,9)
    const chars = 'abcdefghijklmnopqrstuvwxyz12345';
    let result = prefix.toLowerCase().replace(/[^a-z1-5]/g, '').substring(0, 8);

    // Fill remaining characters randomly (max 8 chars before .wam)
    while (result.length < 8) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result + '.wam';
  }
}

export const waxAccountCreator = new WaxAccountCreator();
